import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  apiErrorMessage,
  enrollWinBackAutomation,
  getWinBackAutomation,
  updateWinBackAutomation,
  WinBackAutomation,
} from "../../../helpers/automations_helper";
import { Campaign, getCampaigns } from "../../../helpers/campaigns_helper";
import {
  formatInTenantTimezone,
  readDisplayTimezone,
} from "../../../helpers/tenantTimezone";

type CampaignOption = { value: number; label: string };

/**
 * Win-back opt-in pós-higiene — default off (LAC-145).
 */
const WinBackAutomationPage = () => {
  const { t, i18n } = useTranslation();
  document.title = t("automations.winBack.documentTitle");

  const helpHref = helpUrlForRoute("/automations/win-back");
  const tz = readDisplayTimezone();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<WinBackAutomation | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [maxTouches, setMaxTouches] = useState(2);
  const [cooldownDays, setCooldownDays] = useState(14);
  const [enableConfirmOpen, setEnableConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [winBackRes, list] = await Promise.all([
        getWinBackAutomation(),
        getCampaigns({ has_html: true, per_page: 100 }),
      ]);
      const winBack = winBackRes.data;
      setConfig(winBack);
      setEnabled(!!winBack.enabled);
      setCampaignId(winBack.campaign_id);
      setMaxTouches(winBack.max_touches ?? 2);
      setCooldownDays(winBack.cooldown_days ?? 14);
      setCampaigns(list.data || []);
    } catch (e) {
      setError(apiErrorMessage(e, t("automations.winBack.loadError")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const options: CampaignOption[] = useMemo(
    () =>
      campaigns.map((c) => ({
        value: c.id,
        label: `${c.name} — ${c.subject || t("automations.welcome.noSubject")}`,
      })),
    [campaigns, t]
  );

  const selected = options.find((o) => o.value === campaignId) || null;

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.id === campaignId) ?? null,
    [campaigns, campaignId]
  );

  const metrics = config?.metrics;

  const onEnabledChange = (checked: boolean) => {
    // Ligar com campanha escolhida exige confirmação — consome cota diariamente.
    if (checked && !enabled && campaignId) {
      setEnableConfirmOpen(true);
      return;
    }
    setEnabled(checked);
  };

  const confirmEnable = () => {
    setEnabled(true);
    setEnableConfirmOpen(false);
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await updateWinBackAutomation({
        enabled,
        campaign_id: campaignId,
        max_touches: maxTouches,
        cooldown_days: cooldownDays,
      });
      const next = res.data;
      setConfig(next);
      setEnabled(!!next.enabled);
      setCampaignId(next.campaign_id);
      setMaxTouches(next.max_touches ?? 2);
      setCooldownDays(next.cooldown_days ?? 14);
      toast.success(t("automations.winBack.saved"));
    } catch (e) {
      setError(apiErrorMessage(e, t("automations.winBack.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const onEnroll = async () => {
    setEnrolling(true);
    setError(null);
    try {
      const res = await enrollWinBackAutomation({ days: 90 });
      toast.success(
        t("automations.winBack.enrolledToast", {
          count: res.data?.enrolled ?? 0,
        })
      );
    } catch (e) {
      setError(apiErrorMessage(e, t("automations.winBack.enrollError")));
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("automations.winBack.title")}
          pageTitle={t("menu.automation")}
          actions={
            helpHref ? (
              <PageHelpLink href={helpHref} label={t("automations.winBack.help")} />
            ) : undefined
          }
        />
        <ToastContainer closeButton={false} limit={3} />

        <DismissiblePageTip tipId="win-back-v1">
          {t("automations.winBack.tip")}
        </DismissiblePageTip>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Row>
            <Col lg={8}>
              {error ? <Alert color="danger">{error}</Alert> : null}
              <Card>
                <CardBody>
                  <div className="form-check form-switch mb-3">
                    <Input
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      id="win-back-enabled"
                      checked={enabled}
                      onChange={(e) => onEnabledChange(e.target.checked)}
                    />
                    <Label className="form-check-label" htmlFor="win-back-enabled">
                      {t("automations.winBack.enabledLabel")}
                    </Label>
                  </div>

                  <FormGroup>
                    <Label>{t("automations.winBack.campaignLabel")}</Label>
                    <Select
                      options={options}
                      value={selected}
                      onChange={(opt: CampaignOption | null) =>
                        setCampaignId(opt?.value ?? null)
                      }
                      isClearable
                      placeholder={t("automations.winBack.campaignPlaceholder")}
                    />
                  </FormGroup>

                  {selectedCampaign ? (
                    <Alert color="info" className="py-2">
                      <div className="small">
                        <div>
                          {t("automations.winBack.campaignPreviewName", {
                            name: selectedCampaign.name,
                          })}
                        </div>
                        {selectedCampaign.subject ? (
                          <div className="text-muted">
                            {t("automations.winBack.campaignPreviewSubject", {
                              subject: selectedCampaign.subject,
                            })}
                          </div>
                        ) : null}
                      </div>
                      <Link
                        to={`/campaigns/${selectedCampaign.id}`}
                        className="btn btn-sm btn-soft-primary mt-2"
                      >
                        {t("automations.winBack.openCampaign")}
                      </Link>
                    </Alert>
                  ) : null}

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label htmlFor="win-back-max-touches">
                          {t("automations.winBack.maxTouchesLabel")}
                        </Label>
                        <Input
                          id="win-back-max-touches"
                          type="number"
                          min={1}
                          max={5}
                          value={maxTouches}
                          onChange={(e) => setMaxTouches(Number(e.target.value))}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label htmlFor="win-back-cooldown">
                          {t("automations.winBack.cooldownLabel")}
                        </Label>
                        <Input
                          id="win-back-cooldown"
                          type="number"
                          min={1}
                          max={90}
                          value={cooldownDays}
                          onChange={(e) => setCooldownDays(Number(e.target.value))}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <p className="text-muted small">{t("automations.winBack.freiosHint")}</p>

                  <div className="d-flex flex-wrap gap-2">
                    <Button color="primary" disabled={saving} onClick={onSave}>
                      {saving ? <Spinner size="sm" /> : t("automations.winBack.save")}
                    </Button>
                    <Button
                      color="soft-secondary"
                      disabled={!config?.enabled || enrolling}
                      onClick={onEnroll}
                    >
                      {enrolling ? (
                        <Spinner size="sm" />
                      ) : (
                        t("automations.winBack.enrollNow")
                      )}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {metrics && (
              <Col lg={4}>
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      {t("automations.winBack.metricsTitle")}
                    </h5>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        {t("automations.winBack.metric.sent", {
                          count: metrics.sent_count ?? 0,
                        })}
                      </li>
                      <li className="mb-2">
                        {t("automations.winBack.metric.skipped", {
                          count: metrics.skipped_count ?? 0,
                        })}
                      </li>
                      <li className="mb-2">
                        {t("automations.winBack.metric.pending", {
                          count: metrics.pending_count ?? 0,
                        })}
                      </li>
                      <li className="mb-2">
                        {t("automations.winBack.metric.failed", {
                          count: metrics.failed_count ?? 0,
                        })}
                      </li>
                      <li className="mb-2">
                        {t("automations.winBack.metric.opened", {
                          count: metrics.opened_count ?? 0,
                        })}
                      </li>
                      <li className="mb-0 text-muted small">
                        {metrics.last_enrolled_at
                          ? t("automations.winBack.metric.lastEnrolled", {
                              date: formatInTenantTimezone(
                                metrics.last_enrolled_at,
                                tz,
                                i18n.language
                              ),
                            })
                          : t("automations.winBack.metric.lastEnrolledNone")}
                      </li>
                    </ul>
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        )}

        <Modal
          isOpen={enableConfirmOpen}
          toggle={() => setEnableConfirmOpen(false)}
        >
          <ModalHeader toggle={() => setEnableConfirmOpen(false)}>
            {t("automations.winBack.enableConfirmTitle")}
          </ModalHeader>
          <ModalBody>
            <p className="mb-0">
              {t("automations.winBack.enableConfirmBody", {
                maxTouches,
                cooldownDays,
              })}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setEnableConfirmOpen(false)}>
              {t("automations.winBack.enableConfirmCancel")}
            </Button>
            <Button color="primary" onClick={confirmEnable}>
              {t("automations.winBack.enableConfirmSubmit")}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default WinBackAutomationPage;
